import React, { useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getWidgetMeta } from "@/lib/widgetCatalog";
import { Eye, EyeOff, Lock, Unlock, Boxes } from "lucide-react";
import { cn } from "@/lib/utils";

// Power BI style Selection pane: every widget, hide/lock toggles, click to select.
export function LayersPane({ widgets, selectedIds, onSelect, onToggleHidden, onToggleLock }) {
  const rows = useMemo(() => {
    const sorted = [...widgets].sort((a, b) => (a.layout?.y || 0) - (b.layout?.y || 0) || (a.layout?.x || 0) - (b.layout?.x || 0));
    const seen = new Set();
    const out = [];
    for (const w of sorted) {
      if (w.group_id && !seen.has(w.group_id)) {
        seen.add(w.group_id);
        out.push({ header: w.group_id });
      }
      out.push({ w });
    }
    return out;
  }, [widgets]);

  if (!widgets.length) {
    return <div className="p-4 text-xs text-[color:var(--ink-3)] text-center">No widgets yet. Add some from the Widgets tab.</div>;
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-2 space-y-0.5" data-testid="layers-pane">
        {rows.map((r) =>
          r.header ? (
            <div key={r.header} className="flex items-center gap-1 px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">
              <Boxes className="w-3 h-3" />Group
            </div>
          ) : (
            <LayerRow key={r.w.id} w={r.w} selected={selectedIds.includes(r.w.id)}
              onSelect={onSelect} onToggleHidden={onToggleHidden} onToggleLock={onToggleLock} />
          )
        )}
      </div>
    </ScrollArea>
  );
}

function LayerRow({ w, selected, onSelect, onToggleHidden, onToggleLock }) {
  const meta = getWidgetMeta(w.type);
  return (
    <div
      data-testid="layer-row"
      onClick={(e) => onSelect(w.id, e.ctrlKey || e.metaKey)}
      className={cn(
        "group flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer text-xs transition-colors",
        selected ? "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--ink)]" : "hover:bg-[color:var(--surface-2)] text-[color:var(--ink-2)]",
        w.hidden && "opacity-50",
        w.group_id && "ml-3"
      )}
    >
      <meta.icon className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-ocean)]" />
      <span className="flex-1 truncate">{w.title || meta.label}</span>
      <button data-testid="layer-lock-btn" title={w.locked ? "Unlock position" : "Lock position"}
        onClick={(e) => { e.stopPropagation(); onToggleLock(w); }}
        className={cn("p-0.5 rounded", w.locked ? "text-[color:var(--brand-ocean)]" : "opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
        {w.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
      </button>
      <button data-testid="layer-eye-btn" title={w.hidden ? "Show widget" : "Hide widget"}
        onClick={(e) => { e.stopPropagation(); onToggleHidden(w); }}
        className={cn("p-0.5 rounded", w.hidden ? "text-[color:var(--danger)]" : "opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
        {w.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
    </div>
  );
}
